import * as React from "react";
import {useCallback, useState} from "react";
import {
    Alert,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
} from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import {Bracket} from "../../../data/models/bracket_api/Bracket";
import {Result} from "../../../data/models/bracket_api/Result";
import {Driver} from "../../../data/models/bracket_api/Driver";
import {Event} from "../../../data/models/bracket_api/Event";
import {RaceClass, isTopMomentsRaceClass, raceClassToInt} from "../../../data/models/bracket_api/RaceClass";
import {RaceException, getRaceExceptionFromString} from "../../../data/models/bracket_api/RaceException";
import {fetchBracketByEventName, BracketRun} from "../../../apis/BracketGateway";
import {isGatewayConfigured} from "../../../gatewayConfig";

const ROUND_OPTIONS = ["E1", "E2", "E3", "E4"];

function roundStringToNumber(round: string): number {
    switch (round) {
        case "E1": return 1;
        case "E2": return 2;
        case "E3": return 3;
        case "E4": return 4;
        case "E5": return 5;
        case "E6": return 6;
        case "E7": return 7;
        default: return 1;
    }
}

function toThreeDecimals(value: number | undefined): number | undefined {
    if (value == null) return undefined;
    return value.toFixed(3) as unknown as number;
}

function exceptionFromDisqualification(dq: string | undefined): RaceException {
    if (!dq || dq.trim() === "") return RaceException.NONE;
    if (dq === "RED LT") return RaceException.RED_LIGHT;
    if (dq === "REDLT") return RaceException.RED_LIGHT;
    // Try matching against enum short codes (e.g. "DNS", "RDQ")
    const byCode = Object.values(RaceException).find(v => v === dq);
    if (byCode) return byCode;
    // Try matching against human-readable strings (e.g. "Did Not Start")
    return getRaceExceptionFromString(dq);
}

function mapRunStats(run: BracketRun | undefined): {
    time?: number;
    speed?: number;
    reaction_time?: number;
    exception: RaceException;
} {
    if (!run) {
        return {exception: RaceException.NONE};
    }
    return {
        time: toThreeDecimals(run.et ?? undefined),
        speed: toThreeDecimals(run.mph ?? undefined),
        reaction_time: toThreeDecimals(run.reaction_time ?? undefined),
        exception: exceptionFromDisqualification(run.disqualification),
    };
}

type BracketAutomationFormProps = {
    brackets: Bracket[];
    results: Result[];
    drivers: Driver[];
    event: Event;
    onRoundPopulated: (raceClass: RaceClass, roundNumber: number, updatedMatchups: Result[]) => void;
};

export default function BracketAutomationForm(props: Readonly<BracketAutomationFormProps>) {
    const nonTopMomentsBrackets = props.brackets.filter(b => !isTopMomentsRaceClass(b.race_class));

    const [selectedCategory, setSelectedCategory] = useState<RaceClass | "">("");
    const [selectedRound, setSelectedRound] = useState<string>("");
    const [isPulling, setIsPulling] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "info" | "warning" | "error">("info");

    const showSnackbar = (severity: "success" | "info" | "warning" | "error", message: string) => {
        setSnackbarSeverity(severity);
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const handlePull = useCallback(async () => {
        if (!isGatewayConfigured()) {
            showSnackbar("error", "Bracket gateway service is not configured.");
            return;
        }
        if (!selectedCategory || !selectedRound) {
            showSnackbar("warning", "Please select a category and round.");
            return;
        }

        try {
            setIsPulling(true);
            const categoryInt = raceClassToInt(selectedCategory);
            const response = await fetchBracketByEventName(props.event.name, categoryInt);
            const roundNumber = roundStringToNumber(selectedRound);

            const gatewayRound = response.data.rounds.find(r => r.round === selectedRound);
            if (!gatewayRound || gatewayRound.matchups.length === 0) {
                showSnackbar("info", `No data returned for ${selectedCategory} ${selectedRound}.`);
                return;
            }

            const categoryResults = props.results.filter(
                r => r.bracket.race_class === selectedCategory && r.round === roundNumber
            );

            const updatedMatchups: Result[] = [];

            for (const matchup of gatewayRound.matchups) {
                const existing = categoryResults.find(r => r.game === matchup.matchup_number);
                if (!existing) continue;

                const topStats = mapRunStats(matchup.top_run);
                const botStats = mapRunStats(matchup.bottom_run);

                // Determine winner and loser by drbb_id match
                let winner: Driver | undefined;
                let loser: Driver | undefined;
                let winnerSeed: number | undefined;
                let loserSeed: number | undefined;

                if (matchup.winner) {
                    const winnerDriver = props.drivers.find(d => d.id === matchup.winner!.drbb_id);
                    if (winnerDriver) {
                        winner = winnerDriver;
                        // winner_seed from seed_1 or seed_2 depending on which competitor won
                        if (existing.competitor_1?.id === winnerDriver.id) {
                            winnerSeed = existing.seed_1;
                            loser = existing.competitor_2;
                            loserSeed = existing.seed_2;
                        } else if (existing.competitor_2?.id === winnerDriver.id) {
                            winnerSeed = existing.seed_2;
                            loser = existing.competitor_1;
                            loserSeed = existing.seed_1;
                        } else {
                            // winner not yet seeded into this result (can happen for later rounds populated early)
                            winnerSeed = matchup.winner.qualifying_position;
                        }
                    }
                }

                const updated: Result = {
                    ...existing,
                    competitor_1_time: topStats.time,
                    competitor_1_speed: topStats.speed,
                    competitor_1_reaction_time: topStats.reaction_time,
                    competitor_1_exception: topStats.exception,
                    competitor_2_time: botStats.time,
                    competitor_2_speed: botStats.speed,
                    competitor_2_reaction_time: botStats.reaction_time,
                    competitor_2_exception: botStats.exception,
                    winner,
                    loser,
                    winner_seed: winnerSeed,
                    loser_seed: loserSeed,
                };

                updatedMatchups.push(updated);
            }

            if (updatedMatchups.length === 0) {
                showSnackbar("info", "No matching results found to update for this round.");
                return;
            }

            props.onRoundPopulated(selectedCategory, roundNumber, updatedMatchups);
            showSnackbar("success", `Populated ${updatedMatchups.length} matchup(s) for ${selectedCategory} ${selectedRound}.`);
        } catch (error) {
            console.error(error);
            showSnackbar("error", "Failed to pull bracket data from gateway.");
        } finally {
            setIsPulling(false);
        }
    }, [selectedCategory, selectedRound, props]);

    if (!isGatewayConfigured() || nonTopMomentsBrackets.length === 0) {
        return null;
    }

    return (
        <>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{vertical: "top", horizontal: "center"}}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{width: "100%"}}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <Grid container sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
                padding: "12px",
                justifyContent: "center",
            }}>
                <FormControl sx={{minWidth: 200}}>
                    <InputLabel id="bracket-auto-category-label">Category</InputLabel>
                    <Select<RaceClass | "">
                        labelId="bracket-auto-category-label"
                        label="Category"
                        value={selectedCategory}
                        onChange={e => setSelectedCategory(e.target.value as RaceClass)}
                    >
                        {nonTopMomentsBrackets.map(b => (
                            <MenuItem key={b.race_class} value={b.race_class}>{b.race_class}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{minWidth: 120}}>
                    <InputLabel id="bracket-auto-round-label">Round</InputLabel>
                    <Select
                        labelId="bracket-auto-round-label"
                        label="Round"
                        value={selectedRound}
                        onChange={e => setSelectedRound(e.target.value)}
                    >
                        {ROUND_OPTIONS.map(r => (
                            <MenuItem key={r} value={r}>{r}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    onClick={handlePull}
                    disabled={isPulling || !selectedCategory || !selectedRound}
                    sx={{
                        backgroundColor: "#486ce7",
                        color: "#ffffff",
                        borderRadius: 0,
                        ":hover": {backgroundColor: "#c40000", color: "#ffffff"},
                    }}
                >
                    <Typography variant="h6">
                        {isPulling ? "Pulling..." : "Pull Bracket Round"}
                    </Typography>
                </Button>
            </Grid>
        </>
    );
}
